import { useEffect, useLayoutEffect, useRef } from 'react';
import MobileDashboardNavigation from './MobileDashboardNavigation';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { useDashboardAssistantDock } from '../../contexts/DashboardAssistantDockContext';
import { DASHBOARD_ASSISTANT_ENABLED } from './DashboardAssistantProvider';
import DashboardAssistantChatDock from './DashboardAssistantChatDock';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useSetupChecklistPreferences } from '../../partials/onboarding/checklist/SetupChecklistPreferences';

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return (
    target.isContentEditable ||
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select'
  );
}

function DashboardAssistantBottomBar({ dashboardTimezone, merchantCountryCode, leadingSlot = null, onOpenGettingStarted, sidebarOpen, onToggleSidebar, onCloseSidebar }) {
  const { openAssistantOrResume, resumeAssistant, isExpanded, assistance } = useDashboardAssistantDock();
  const { isCompact, surface, helpError, isProductlaneOpen, closeHelpMenu, openHelp, closeWidget, dismissSurfaces } = assistance;
  const helpTrigger = useRef(null);
  const nextHelpAction = useRef(null);
  const barRef = useRef(null);
  const openingChecklist = useRef(false);
  const checklist = useSetupChecklistPreferences();

  useEffect(() => {
    if (!DASHBOARD_ASSISTANT_ENABLED) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const isMacShortcut = event.metaKey && !event.ctrlKey && event.key.toLowerCase() === 'j';
      const isWindowsShortcut = event.ctrlKey && !event.metaKey && event.key.toLowerCase() === 'j';

      if (!isMacShortcut && !isWindowsShortcut) {
        return;
      }

      if (event.altKey || event.shiftKey || isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      if (isCompact) { onCloseSidebar?.(); resumeAssistant(); }
      else openAssistantOrResume();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCompact, openAssistantOrResume, resumeAssistant, onCloseSidebar]);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const root = document.documentElement;
    const updateBottomBarHeight = () => {
      const height = barRef.current?.getBoundingClientRect().height || 0;
      const value = height > 0 ? `${Math.ceil(height)}px` : '0px';
      root.style.setProperty('--dashboard-assistant-bottom-bar-height', value);
      root.style.setProperty('--dashboard-shell-bottom-bar-height', value);
    };

    updateBottomBarHeight();

    const observer =
      typeof ResizeObserver !== 'undefined' && barRef.current
        ? new ResizeObserver(updateBottomBarHeight)
        : null;
    observer?.observe(barRef.current);
    window.addEventListener('resize', updateBottomBarHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateBottomBarHeight);
      root.style.setProperty('--dashboard-assistant-bottom-bar-height', '0px');
      root.style.setProperty('--dashboard-shell-bottom-bar-height', '0px');
    };
  }, []);

  const handleOpenProductlane = () => {
    if (isProductlaneOpen) closeWidget();
    else openHelp();
  };
  const openChecklist = () => {
    dismissSurfaces();
    if (isCompact) onOpenGettingStarted?.();
    checklist?.reopen();
  };

  return (
    <div
      ref={barRef}
      className={`relative z-[45] w-full shrink-0 bg-paper-muted ${isCompact ? 'px-2 pt-2' : 'px-4 py-2'} ${isCompact && isExpanded ? 'invisible' : ''}`}
      style={{ paddingBottom: isCompact ? 'max(8px, env(safe-area-inset-bottom))' : undefined }}
    >
      <TooltipProvider delayDuration={150}>
        <div className={`relative flex min-h-[36px] min-w-0 gap-2 items-center ${isCompact ? 'mx-auto w-full md:max-w-[640px]' : ''}`}>
          {leadingSlot && !isCompact ? (
            <div className={isCompact ? "absolute bottom-full left-0 mb-2" : "flex min-w-0 shrink-0 items-center"}>{leadingSlot}</div>
          ) : null}
            <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 lg:gap-2">
              {isCompact && <MobileDashboardNavigation sidebarOpen={sidebarOpen} onToggleSidebar={onToggleSidebar} onCloseSidebar={onCloseSidebar} dismissSurfaces={dismissSurfaces} />}
              {DASHBOARD_ASSISTANT_ENABLED && <DashboardAssistantChatDock dashboardTimezone={dashboardTimezone} merchantCountryCode={merchantCountryCode} onCompactOpen={onCloseSidebar} />}
              {isCompact ? <>
                <Dialog open={surface === 'help-menu'} onOpenChange={open => { if (!open) closeHelpMenu(); }}>
                  <DialogContent className="top-auto bottom-0 w-full max-w-none translate-y-0 gap-3 rounded-t-lg p-4 pb-[max(16px,env(safe-area-inset-bottom))] sm:rounded-t-lg" onCloseAutoFocus={event => {
                    event.preventDefault();
                    const action = nextHelpAction.current;
                    nextHelpAction.current = null;
                    if (action) action();
                    else if (!isExpanded) (helpTrigger.current || document.querySelector('[data-mobile-more]'))?.focus();
                  }}>
                    <DialogTitle>How can we help?</DialogTitle>
                    {helpError && <p role="alert" className="text-sm text-ink-secondary">{helpError}</p>}
                    <Button variant="neutral" className="justify-start" onClick={() => { nextHelpAction.current = openHelp; closeHelpMenu(); }}>{helpError ? 'Try Help Center again' : 'Open Help Center'}</Button>
                    {checklist && <Button variant="neutral" className="justify-start" onClick={() => { nextHelpAction.current = openChecklist; closeHelpMenu(); }}>Getting started</Button>}
                  </DialogContent>
                </Dialog>
              </> : <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                  <button
                    className="relative z-10 flex h-8 shrink-0 touch-manipulation select-none items-center justify-center gap-2 rounded-lg border border-edge/80 bg-white px-2.5 text-ink shadow-sm transition hover:bg-paper-light"
                    aria-label="Open help menu"
                    type="button"
                  >
                    {isProductlaneOpen ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="pointer-events-none h-4 w-4 text-ink-muted"
                      >
                        <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="pointer-events-none h-4 w-4 text-ink-muted"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0ZM9.112 6.223c-.57.26-.934.736-1.12 1.338a.75.75 0 0 1-1.433-.442 3.252 3.252 0 0 1 2.265-2.263c1.209-.33 2.7-.004 3.55 1.287.808 1.23.46 3.058-.83 3.91-.47.31-.722.54-.88.76-.144.2-.218.399-.267.707a.75.75 0 0 1-1.48-.236c.073-.457.203-.887.53-1.342.313-.434.748-.798 1.27-1.143.62-.41.768-1.337.403-1.893-.323-.49-.99-.793-2.008-.683ZM10 13.75a.875.875 0 1 0 0 1.75.875.875 0 0 0 0-1.75Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span className="pointer-events-none whitespace-nowrap text-sm font-medium text-ink-secondary">
                      Help Center
                    </span>
                  </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Help Center
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent side="top" align="end" className="w-52" onCloseAutoFocus={(event) => {
                if (openingChecklist.current) {
                  event.preventDefault();
                  openingChecklist.current = false;
                  openChecklist();
                }
              }}>
                <DropdownMenuItem onSelect={handleOpenProductlane}>{isProductlaneOpen ? 'Close Help Center' : 'Open Help Center'}</DropdownMenuItem>
                {checklist && <DropdownMenuItem onSelect={() => {
                  openingChecklist.current = true;
                }}>Getting started</DropdownMenuItem>}
              </DropdownMenuContent>
              </DropdownMenu>}
            </div>
        </div>
        {!isCompact && helpError && <p role="alert" className="text-right text-sm text-ink-secondary">{helpError} <button type="button" onClick={openHelp} className="underline">Try again</button></p>}
      </TooltipProvider>
    </div>
  );
}

export default DashboardAssistantBottomBar;
